import { useEffect } from 'react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../config/config-firebase';
import { fsRolesCol, fsUsersCol } from '../config/config-firestore';
import roleConfig from '../config/config.role';
import AuthAction from '../reduxStore/authReducer/authAction';
import { useAppDispatch } from '../reduxStore/utils/reduxHooks';

const authListener = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let unsubscribeUser: (() => void) | null = null;
    dispatch({ type: AuthAction.SET_AUTH_READY, payload: false });
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }

      if (!user) {
        dispatch({ type: AuthAction.CLEAR_USER, payload: null });
        dispatch({ type: AuthAction.SET_ROLE, payload: roleConfig.anonymous });
        dispatch({ type: AuthAction.SET_AUTH_READY, payload: true });
        return;
      }

      const userRef = doc(db, fsUsersCol, user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          role: roleConfig.reader,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(
          userRef,
          {
            email: user.email || '',
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }

      unsubscribeUser = onSnapshot(userRef, (snap) => {
        if (!snap.exists()) {
          return;
        }
        const data = snap.data() as { role?: string };
        dispatch({
          type: AuthAction.SET_USER,
          payload: {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
          },
        });
        dispatch({
          type: AuthAction.SET_ROLE,
          payload: data.role || roleConfig.reader,
        });
        dispatch({ type: AuthAction.SET_AUTH_READY, payload: true });
      });
    });

    return () => {
      if (unsubscribeUser) {
        unsubscribeUser();
      }
      unsubscribeAuth();
    };
  }, [dispatch]);

  useEffect(() => {
    const rolesRef = collection(db, fsRolesCol);
    const unsubscribeRoles = onSnapshot(rolesRef, (snap) => {
      const rolePages = snap.docs.reduce<{ [key: string]: string[] }>(
        (acc, docItem) => {
          const data = docItem.data() as { pages?: string[] };
          acc[docItem.id] = data.pages || [];
          return acc;
        },
        {},
      );
      dispatch({
        type: AuthAction.SET_ROLE_PAGES,
        payload: rolePages,
      });
    });

    return () => {
      unsubscribeRoles();
    };
  }, [dispatch]);

  return null;
};

export default authListener;
